import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FileBarChart, Download, Filter, Calendar, Search, Eye, FileSpreadsheet, FileText, Image, Cpu, TrendingUp, TrendingDown, Zap, DollarSign, Activity } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Reports() {
  const [reports, setReports] = useState({ report_data: [], departments: [] });
  const [llmUsage, setLlmUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [llmLoading, setLlmLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("sessions"); // "sessions" or "llm-usage"
  const [filters, setFilters] = useState({
    month: new Date().toISOString().slice(0, 7),
    department: "all"
  });

  useEffect(() => {
    fetchReports();
    fetchLlmUsage();
  }, [filters]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      let url = `${API}/analytics/reports?month=${filters.month}`;
      if (filters.department !== "all") {
        url += `&department=${filters.department}`;
      }
      const response = await axios.get(url);
      setReports(response.data);
    } catch (error) {
      console.error("Failed to fetch reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLlmUsage = async () => {
    setLlmLoading(true);
    try {
      const response = await axios.get(`${API}/admin/llm-usage?month=${filters.month}`);
      setLlmUsage(response.data);
    } catch (error) {
      console.error("Failed to fetch LLM usage:", error);
    } finally {
      setLlmLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      analyzed: "badge-success",
      completed: "badge-info",
      in_progress: "badge-warning",
      created: "badge-warning"
    };
    return styles[status] || "badge-info";
  };

  const generateMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        value: date.toISOString().slice(0, 7),
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      });
    }
    return months;
  };

  // Filter by search query
  const filteredData = reports.report_data?.filter(row =>
    row.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    row.topic?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    row.department?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Export to CSV function
  const exportToCSV = () => {
    if (filteredData.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = ["User", "Department", "Topic", "Status", "Score", "Session Date", "Last Accessed"];
    const csvRows = [
      headers.join(","),
      ...filteredData.map(row => [
        `"${row.user_name || ''}"`,
        `"${row.department || ''}"`,
        `"${row.topic || ''}"`,
        `"${row.status || ''}"`,
        row.score || '',
        new Date(row.created_at).toLocaleDateString(),
        row.last_accessed ? new Date(row.last_accessed).toLocaleString() : ''
      ].join(","))
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `evolve_report_${filters.month}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${filteredData.length} records to CSV`);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Reports & Analytics
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            View sessions, presentations, and AI credit usage
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid="export-csv-btn"
            onClick={() => exportToCSV()}
            className="rounded-full"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab("sessions")}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "sessions"
              ? "text-primary border-b-2 border-primary"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <FileBarChart className="w-4 h-4 inline mr-2" />
          Sessions
        </button>
        <button
          onClick={() => setActiveTab("llm-usage")}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "llm-usage"
              ? "text-primary border-b-2 border-primary"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Cpu className="w-4 h-4 inline mr-2" />
          LLM Credit Usage
        </button>
      </div>

      {/* Month Filter - Common for both tabs */}
      <Card className="stats-card">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Month:</span>
            </div>
            <Select
              value={filters.month}
              onValueChange={(value) => setFilters({ ...filters, month: value })}
            >
              <SelectTrigger data-testid="month-filter" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {generateMonthOptions().map((month) => (
                  <SelectItem key={month.value} value={month.value}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {activeTab === "sessions" && (
              <>
                <div className="relative flex-1 min-w-[200px] max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="search-reports"
                    placeholder="Search topics, users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select
                  value={filters.department}
                  onValueChange={(value) => setFilters({ ...filters, department: value })}
                >
                  <SelectTrigger data-testid="department-filter" className="w-48">
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {reports.departments?.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sessions Tab Content */}
      {activeTab === "sessions" && (
        <Card className="stats-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <FileBarChart className="w-5 h-5 text-primary" />
              All Presentations ({filteredData.length} sessions)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Department</th>
                      <th>Topic</th>
                      <th>Status</th>
                      <th>Score</th>
                      <th>Session Date</th>
                      <th>Last Accessed</th>
                      <th>Presentation</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((row, index) => (
                      <tr key={row.session_id} data-testid={`report-row-${index}`}>
                        <td className="font-medium">{row.user_name}</td>
                        <td className="text-[#5C5C5C]">{row.department}</td>
                        <td>
                          <div className="max-w-xs">
                            <p className="font-medium truncate">{row.topic}</p>
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${getStatusBadge(row.status)}`}>
                            {row.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          {row.score ? (
                            <span className={`font-bold ${
                              row.score >= 80 ? 'text-[#059669]' :
                              row.score >= 60 ? 'text-[#2563EB]' :
                              'text-[#D97706]'
                            }`}>
                              {row.score}
                            </span>
                          ) : (
                            <span className="text-[#9CA3AF]">-</span>
                          )}
                        </td>
                        <td className="text-[#5C5C5C] text-sm">
                          {new Date(row.created_at).toLocaleDateString()}
                        </td>
                        <td className="text-[#5C5C5C] text-sm">
                          {row.last_accessed ? (
                            new Date(row.last_accessed).toLocaleString([], {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          ) : (
                            <span className="text-[#9CA3AF]">-</span>
                          )}
                        </td>
                        <td>
                          {row.has_presentation !== false && (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(`${API}/sessions/${row.session_id}/slides`, '_blank')}
                                className="h-8 px-2"
                                title="Preview Slides"
                              >
                                <Image className="w-4 h-4 text-[#8B5CF6]" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(`${API}/sessions/${row.session_id}/download-presentation`, '_blank')}
                                className="h-8 px-2"
                                title="Download PPT"
                              >
                                <Download className="w-4 h-4 text-[#059669]" />
                              </Button>
                            </div>
                          )}
                        </td>
                        <td>
                          {row.status === 'analyzed' && (
                            <Link to={`/session/${row.session_id}/feedback`}>
                              <Button variant="ghost" size="sm" className="rounded-full">
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </Link>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state py-12">
                <FileBarChart className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-[#5C5C5C]">No sessions found</p>
                <p className="text-sm text-[#9CA3AF] mt-1">
                  {searchQuery ? "Try adjusting your search" : "Sessions will appear here once created"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* LLM Usage Tab Content */}
      {activeTab === "llm-usage" && (
        <div className="space-y-6">
          {llmLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : llmUsage ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="stats-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Cost</p>
                        <p className="text-2xl font-bold text-[#059669]">
                          ${llmUsage.summary.total_cost.toFixed(4)}
                        </p>
                      </div>
                      <DollarSign className="w-8 h-8 text-[#059669] opacity-50" />
                    </div>
                    <div className="mt-2 flex items-center text-xs">
                      {llmUsage.summary.cost_change_percent > 0 ? (
                        <>
                          <TrendingUp className="w-3 h-3 text-red-500 mr-1" />
                          <span className="text-red-500">+{llmUsage.summary.cost_change_percent}%</span>
                        </>
                      ) : llmUsage.summary.cost_change_percent < 0 ? (
                        <>
                          <TrendingDown className="w-3 h-3 text-green-500 mr-1" />
                          <span className="text-green-500">{llmUsage.summary.cost_change_percent}%</span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">No change</span>
                      )}
                      <span className="text-muted-foreground ml-1">vs last month</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="stats-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Tokens</p>
                        <p className="text-2xl font-bold text-[#2563EB]">
                          {llmUsage.summary.total_tokens.toLocaleString()}
                        </p>
                      </div>
                      <Zap className="w-8 h-8 text-[#2563EB] opacity-50" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Input + Output combined
                    </p>
                  </CardContent>
                </Card>

                <Card className="stats-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">API Calls</p>
                        <p className="text-2xl font-bold text-[#8B5CF6]">
                          {llmUsage.summary.total_calls}
                        </p>
                      </div>
                      <Activity className="w-8 h-8 text-[#8B5CF6] opacity-50" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      This month
                    </p>
                  </CardContent>
                </Card>

                <Card className="stats-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Avg Cost/Call</p>
                        <p className="text-2xl font-bold text-[#D97706]">
                          ${llmUsage.summary.total_calls > 0 
                            ? (llmUsage.summary.total_cost / llmUsage.summary.total_calls).toFixed(4) 
                            : '0.0000'}
                        </p>
                      </div>
                      <Cpu className="w-8 h-8 text-[#D97706] opacity-50" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Per API request
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Daily Usage Chart */}
              {llmUsage.daily_usage && llmUsage.daily_usage.length > 0 && (
                <Card className="stats-card">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <Activity className="w-5 h-5 text-primary" />
                      Daily Usage Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={llmUsage.daily_usage}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip 
                            formatter={(value, name) => [
                              name === 'cost' ? `$${value.toFixed(4)}` : value.toLocaleString(),
                              name === 'cost' ? 'Cost' : name === 'tokens' ? 'Tokens' : 'Calls'
                            ]}
                            labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          />
                          <Line type="monotone" dataKey="tokens" stroke="#2563EB" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Usage by Operation and Model */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* By Operation */}
                <Card className="stats-card">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <Zap className="w-5 h-5 text-primary" />
                      Usage by Feature
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {llmUsage.by_operation && llmUsage.by_operation.length > 0 ? (
                      <div className="space-y-3">
                        {llmUsage.by_operation.map((op) => (
                          <div key={op.operation} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                            <div>
                              <p className="font-medium capitalize">
                                {op.operation.replace(/_/g, ' ')}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {op.count} calls • {op.total_tokens.toLocaleString()} tokens
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-[#059669]">${op.total_cost.toFixed(4)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">No usage data yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* By Model */}
                <Card className="stats-card">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <Cpu className="w-5 h-5 text-primary" />
                      Usage by Model
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {llmUsage.by_model && llmUsage.by_model.length > 0 ? (
                      <div className="space-y-3">
                        {llmUsage.by_model.map((model) => (
                          <div key={model.model} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                            <div>
                              <p className="font-medium">{model.model}</p>
                              <p className="text-xs text-muted-foreground">
                                {model.count} calls • {model.total_tokens.toLocaleString()} tokens
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-[#059669]">${model.total_cost.toFixed(4)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">No usage data yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity */}
              {llmUsage.recent_records && llmUsage.recent_records.length > 0 && (
                <Card className="stats-card">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <FileBarChart className="w-5 h-5 text-primary" />
                      Recent LLM Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Operation</th>
                            <th>Model</th>
                            <th>Input Tokens</th>
                            <th>Output Tokens</th>
                            <th>Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {llmUsage.recent_records.slice().reverse().map((record, idx) => (
                            <tr key={record.id || idx}>
                              <td className="text-sm text-muted-foreground">
                                {new Date(record.timestamp).toLocaleString([], {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </td>
                              <td className="capitalize">{record.operation?.replace(/_/g, ' ')}</td>
                              <td><code className="text-xs bg-muted px-1 py-0.5 rounded">{record.model}</code></td>
                              <td>{record.input_tokens?.toLocaleString() || 0}</td>
                              <td>{record.output_tokens?.toLocaleString() || 0}</td>
                              <td className="font-medium text-[#059669]">${record.cost_estimate?.toFixed(4) || '0.0000'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="stats-card">
              <CardContent className="py-12">
                <div className="empty-state">
                  <Cpu className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-[#5C5C5C]">No LLM usage data available</p>
                  <p className="text-sm text-[#9CA3AF] mt-1">
                    Usage will be tracked when AI features are used
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
